import { useDroppable } from "@dnd-kit/core";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Wallet } from "@/types";
import { KanbanCard } from "./KanbanCard";
import type { KanbanStage, SortMode } from "./kanbanConfig";

interface KanbanColumnProps {
	stage: KanbanStage;
	wallets: Wallet[];
	sortMode: SortMode;
}

export function KanbanColumn({ stage, wallets, sortMode }: KanbanColumnProps) {
	const { setNodeRef, isOver } = useDroppable({ id: stage.id });

	const sorted = [...wallets].sort((a, b) => {
		if (sortMode === "alpha-asc")
			return a.wallet_name.localeCompare(b.wallet_name);
		if (sortMode === "alpha-desc")
			return b.wallet_name.localeCompare(a.wallet_name);
		return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
	});

	return (
		<div
			ref={setNodeRef}
			className={cn(
				"flex min-w-[160px] flex-1 flex-col rounded-(--radius-sm) border bg-(--color-muted) transition-colors",
				isOver
					? "border-(--color-primary) bg-(--color-primary)/5"
					: "border-(--color-border)",
			)}
		>
			{/* Color strip */}
			<div
				className={cn("h-[3px] rounded-t-(--radius-sm)", stage.colorClass)}
			/>

			{/* Header */}
			<div className="flex items-center justify-between px-3 py-2.5">
				<span className="font-semibold text-(--color-foreground) text-sm">
					{stage.label}
				</span>
				<Badge variant={stage.badgeVariant} className="text-[0.625rem]">
					{wallets.length}
				</Badge>
			</div>

			{/* Cards */}
			<div className="flex max-h-[calc(100vh-290px)] flex-1 flex-col gap-2.5 overflow-y-auto px-2.5 pb-2.5">
				{sorted.length === 0 ? (
					<p className="py-6 text-center text-(--color-muted-foreground) text-xs italic">
						No partners in this stage
					</p>
				) : (
					sorted.map((wallet) => <KanbanCard key={wallet.id} wallet={wallet} />)
				)}
			</div>
		</div>
	);
}
